import axios from "axios";
import store from "../store/index.js";
import { logout } from "../store/userSlice.js";

const apiClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue = [];
// Module-level guard so a refresh failure during a multi-request fan-out
// only fires logout + redirect once.
let alreadyLoggedOut = false;

const processQueue = (error, shouldRetry = true) => {
  failedQueue.forEach((prom) => {
    if (shouldRetry) {
      prom.resolve();
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

// Endpoints whose 401 should NOT trigger a global logout cascade.
// Failing these is non-fatal — the page can render without them.
// auth/me is intentionally NOT here: it's the canonical session check, so a
// 401 there must trigger /auth/refresh (otherwise access_token expiry at
// 15min would log the user out instead of silently rotating).
const SOFT_AUTH_PATHS = [
  "admin/analytics",
  "public/config",
  "public/settings",
  "auth/fcm-token",
];

const isSoftAuthPath = (url) => {
  if (!url) return false;
  return SOFT_AUTH_PATHS.some((path) => url.includes(path));
};

const performLogout = () => {
  if (alreadyLoggedOut) return;
  alreadyLoggedOut = true;
  store.dispatch(logout());

  if (typeof window !== "undefined") {
    const pathname = window.location.pathname;

    // Only redirect to login if the user is currently on a protected dashboard route.
    // Public routes (like /f/[token], /about-us, etc.) should stay as they are (anonymous).
    const protectedPrefixes = [
      "/admin",
      "/hr",
      "/employee",
      "/marketing-manager",
      "/marketing-executive",
      "/client",
      "/lead",
      "/dashboard",
    ];

    const isDashboardPath = protectedPrefixes.some((prefix) =>
      pathname.startsWith(prefix)
    );

    if (isDashboardPath && !pathname.startsWith("/auth/")) {
      window.location.replace("/auth/login");
    }
  }
};

// Reset the logout guard on a fresh successful login. Anything in the app that
// dispatches `login()` should also reset this — easiest is to import & call.
export const resetAuthGuard = () => {
  alreadyLoggedOut = false;
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;

    // Only handle 401s that aren't already mid-retry
    if (status !== 401 || originalRequest._retry || originalRequest._skipAuthRefresh) {
      return Promise.reject(error);
    }

    // If the user has already been kicked, don't retry — just reject.
    if (alreadyLoggedOut) {
      return Promise.reject(error);
    }

    // Soft endpoints: let the call fail; do NOT trigger refresh + logout cascade.
    if (isSoftAuthPath(originalRequest.url)) {
      return Promise.reject(error);
    }

    // Queue parallel requests while one refresh is in flight.
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => apiClient(originalRequest));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      await apiClient.post("auth/refresh", null, { _skipAuthRefresh: true });
      processQueue(null, true);
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, false);
      // Refresh genuinely failed — session is dead. Logout exactly once.
      performLogout();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default apiClient;
